import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, LogOut, Users, Briefcase, FileText, Settings, DollarSign,
  Menu, X, TrendingUp, Eye, Edit, Trash2, Plus, Check, XCircle,
  Package, Wrench, Tags
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const UNIT_TYPES = [
  { value: "sq_ft", label: "Square Feet" },
  { value: "linear_ft", label: "Linear Feet" },
  { value: "per_room", label: "Per Room" },
  { value: "per_door", label: "Per Door" },
  { value: "per_window", label: "Per Window" },
  { value: "per_fixture", label: "Per Fixture" },
  { value: "per_cabinet", label: "Per Cabinet" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_frame", label: "Per Frame" },
  { value: "per_closet", label: "Per Closet" },
  { value: "per_hallway", label: "Per Hallway" }
];

export default function AdminCatalog() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("items");
  const [lineItems, setLineItems] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Dialogs
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  
  const [editingItem, setEditingItem] = useState(null);
  const [editingBundle, setEditingBundle] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  
  const [itemForm, setItemForm] = useState({
    name: '', category: 'Painting', subcategory: 'Interior',
    pricing_mode: 'flat', unit_type: '',
    labor_low: 0, labor_high: 0, materials_low: 0, materials_high: 0,
    description: '', is_active: true, sort_order: 0
  });
  
  const [bundleForm, setBundleForm] = useState({
    name: '', description: '', category: 'Painting', items: [],
    pricing_type: 'derived',
    bundle_labor_low: 0, bundle_labor_high: 0,
    bundle_materials_low: 0, bundle_materials_high: 0,
    is_active: true
  });
  
  const [addonForm, setAddonForm] = useState({
    name: '', description: '', modifier_type: 'percent',
    modifier_value: 0, applies_to: 'both', category: '', is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, bundlesRes, addonsRes] = await Promise.all([
        axios.get(`${API}/catalog/line-items`),
        axios.get(`${API}/catalog/bundles`),
        axios.get(`${API}/catalog/addons`)
      ]);
      setLineItems(itemsRes.data);
      setBundles(bundlesRes.data);
      setAddons(addonsRes.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Line Items CRUD
  const openItemDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        name: item.name,
        category: item.category,
        subcategory: item.subcategory,
        pricing_mode: item.pricing_mode,
        unit_type: item.unit_type || '',
        labor_low: item.labor_low,
        labor_high: item.labor_high,
        materials_low: item.materials_low,
        materials_high: item.materials_high,
        description: item.description || '',
        is_active: item.is_active,
        sort_order: item.sort_order || 0
      });
    } else {
      setEditingItem(null);
      setItemForm({
        name: '', category: 'Painting', subcategory: 'Interior',
        pricing_mode: 'flat', unit_type: '',
        labor_low: 0, labor_high: 0, materials_low: 0, materials_high: 0,
        description: '', is_active: true, sort_order: 0
      });
    }
    setShowItemDialog(true);
  };

  const saveItem = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/catalog/line-items/${editingItem.item_id}`, itemForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/admin/catalog/line-items`, itemForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Item created");
      }
      setShowItemDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    }
  };

  const deleteItem = async (itemId) => {
    if (!confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/catalog/line-items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  // Bundles CRUD
  const openBundleDialog = (bundle = null) => {
    if (bundle) {
      setEditingBundle(bundle);
      setBundleForm({
        name: bundle.name,
        description: bundle.description || '',
        category: bundle.category,
        items: bundle.items || [],
        pricing_type: bundle.pricing_type,
        bundle_labor_low: bundle.bundle_labor_low || 0,
        bundle_labor_high: bundle.bundle_labor_high || 0,
        bundle_materials_low: bundle.bundle_materials_low || 0,
        bundle_materials_high: bundle.bundle_materials_high || 0,
        is_active: bundle.is_active
      });
    } else {
      setEditingBundle(null);
      setBundleForm({
        name: '', description: '', category: 'Painting', items: [],
        pricing_type: 'derived',
        bundle_labor_low: 0, bundle_labor_high: 0,
        bundle_materials_low: 0, bundle_materials_high: 0,
        is_active: true
      });
    }
    setShowBundleDialog(true);
  };

  const saveBundle = async () => {
    try {
      if (editingBundle) {
        await axios.put(`${API}/admin/catalog/bundles/${editingBundle.bundle_id}`, bundleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Bundle updated");
      } else {
        await axios.post(`${API}/admin/catalog/bundles`, bundleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Bundle created");
      }
      setShowBundleDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bundle");
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!confirm("Delete this bundle?")) return;
    try {
      await axios.delete(`${API}/admin/catalog/bundles/${bundleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bundle deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete bundle");
    }
  };

  // Add-ons CRUD
  const openAddonDialog = (addon = null) => {
    if (addon) {
      setEditingAddon(addon);
      setAddonForm({
        name: addon.name,
        description: addon.description || '',
        modifier_type: addon.modifier_type,
        modifier_value: addon.modifier_value,
        applies_to: addon.applies_to,
        category: addon.category || '',
        is_active: addon.is_active
      });
    } else {
      setEditingAddon(null);
      setAddonForm({
        name: '', description: '', modifier_type: 'percent',
        modifier_value: 0, applies_to: 'both', category: '', is_active: true
      });
    }
    setShowAddonDialog(true);
  };

  const saveAddon = async () => {
    try {
      const data = { ...addonForm, category: addonForm.category || null };
      if (editingAddon) {
        await axios.put(`${API}/admin/catalog/addons/${editingAddon.addon_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Add-on updated");
      } else {
        await axios.post(`${API}/admin/catalog/addons`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Add-on created");
      }
      setShowAddonDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save add-on");
    }
  };

  const deleteAddon = async (addonId) => {
    if (!confirm("Delete this add-on?")) return;
    try {
      await axios.delete(`${API}/admin/catalog/addons/${addonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Add-on deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete add-on");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-catalog-page">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-manrope font-bold text-[#1F2933]">Catalog Manager</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-white">Contractor Hub LLC</span>
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">{user?.name}</p>
                <p className="text-white/60 text-xs">Administrator</p>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            <Link to="/admin" className="sidebar-link w-full text-left">
              <TrendingUp className="w-5 h-5" /> Dashboard
            </Link>
            <button className="sidebar-link active w-full text-left">
              <Wrench className="w-5 h-5" /> Catalog Manager
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-300 hover:bg-red-500/20"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
            Pricing Catalog Manager
          </h1>
          <p className="text-[#1F2933]/60">Manage line items, bundles, and add-ons</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="items" className="gap-2">
                <Wrench className="w-4 h-4" /> Line Items ({lineItems.length})
              </TabsTrigger>
              <TabsTrigger value="bundles" className="gap-2">
                <Package className="w-4 h-4" /> Bundles ({bundles.length})
              </TabsTrigger>
              <TabsTrigger value="addons" className="gap-2">
                <Tags className="w-4 h-4" /> Add-ons ({addons.length})
              </TabsTrigger>
            </TabsList>

            {/* Line Items Tab */}
            <TabsContent value="items">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-[#1F2933]">Line Items</h2>
                  <Button 
                    onClick={() => openItemDialog()}
                    className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Item
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Pricing Mode</TableHead>
                        <TableHead>Labor (Low-High)</TableHead>
                        <TableHead>Materials (Low-High)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item) => (
                        <TableRow key={item.item_id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.category} / {item.subcategory}</Badge>
                          </TableCell>
                          <TableCell>
                            {item.pricing_mode === 'flat' ? 'Flat' : `Per ${item.unit_type?.replace('_', ' ')}`}
                          </TableCell>
                          <TableCell>${item.labor_low} - ${item.labor_high}</TableCell>
                          <TableCell>${item.materials_low} - ${item.materials_high}</TableCell>
                          <TableCell>
                            <Badge className={item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {item.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openItemDialog(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteItem(item.item_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Bundles Tab */}
            <TabsContent value="bundles">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-[#1F2933]">Bundles / Packages</h2>
                  <Button 
                    onClick={() => openBundleDialog()}
                    className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                    data-testid="add-bundle-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Bundle
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Pricing Type</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bundles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12 text-[#1F2933]/50">
                            No bundles created yet
                          </TableCell>
                        </TableRow>
                      ) : bundles.map((bundle) => (
                        <TableRow key={bundle.bundle_id}>
                          <TableCell className="font-medium">{bundle.name}</TableCell>
                          <TableCell>{bundle.category}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{bundle.pricing_type === 'fixed' ? 'Fixed Price' : 'Derived'}</Badge>
                          </TableCell>
                          <TableCell>{bundle.items?.length || 0} items</TableCell>
                          <TableCell>
                            <Badge className={bundle.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {bundle.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openBundleDialog(bundle)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteBundle(bundle.bundle_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Add-ons Tab */}
            <TabsContent value="addons">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-[#1F2933]">Add-ons / Modifiers</h2>
                  <Button 
                    onClick={() => openAddonDialog()}
                    className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                    data-testid="add-addon-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Add-on
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Applies To</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {addons.map((addon) => (
                        <TableRow key={addon.addon_id}>
                          <TableCell className="font-medium">{addon.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{addon.modifier_type === 'percent' ? 'Percentage' : 'Flat'}</Badge>
                          </TableCell>
                          <TableCell>
                            {addon.modifier_type === 'percent' ? `${addon.modifier_value}%` : `$${addon.modifier_value}`}
                          </TableCell>
                          <TableCell className="capitalize">{addon.applies_to}</TableCell>
                          <TableCell>{addon.category || 'All'}</TableCell>
                          <TableCell>
                            <Badge className={addon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {addon.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openAddonDialog(addon)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteAddon(addon.addon_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Line Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Line Item' : 'Add Line Item'}</DialogTitle>
            <DialogDescription>Configure pricing for this service item</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g., Walls"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Input
                  value={itemForm.category}
                  onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                  placeholder="e.g., Painting"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subcategory *</Label>
                <Input
                  value={itemForm.subcategory}
                  onChange={(e) => setItemForm({ ...itemForm, subcategory: e.target.value })}
                  placeholder="e.g., Interior"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={itemForm.sort_order}
                  onChange={(e) => setItemForm({ ...itemForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pricing Mode *</Label>
                <Select 
                  value={itemForm.pricing_mode} 
                  onValueChange={(v) => setItemForm({ ...itemForm, pricing_mode: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat Price</SelectItem>
                    <SelectItem value="unit_based">Unit-Based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {itemForm.pricing_mode === 'unit_based' && (
                <div>
                  <Label>Unit Type *</Label>
                  <Select 
                    value={itemForm.unit_type} 
                    onValueChange={(v) => setItemForm({ ...itemForm, unit_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_TYPES.map(u => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Labor Low ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.labor_low}
                  onChange={(e) => setItemForm({ ...itemForm, labor_low: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Labor High ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.labor_high}
                  onChange={(e) => setItemForm({ ...itemForm, labor_high: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Materials Low ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.materials_low}
                  onChange={(e) => setItemForm({ ...itemForm, materials_low: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Materials High ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.materials_high}
                  onChange={(e) => setItemForm({ ...itemForm, materials_high: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                className="mt-1"
                placeholder="Optional description..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={itemForm.is_active}
                onCheckedChange={(v) => setItemForm({ ...itemForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={saveItem} className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle Dialog */}
      <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBundle ? 'Edit Bundle' : 'Add Bundle'}</DialogTitle>
            <DialogDescription>Create a package with multiple line items</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={bundleForm.name}
                onChange={(e) => setBundleForm({ ...bundleForm, name: e.target.value })}
                placeholder="e.g., Full Room Package"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Input
                  value={bundleForm.category}
                  onChange={(e) => setBundleForm({ ...bundleForm, category: e.target.value })}
                  placeholder="e.g., Painting"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Pricing Type *</Label>
                <Select 
                  value={bundleForm.pricing_type} 
                  onValueChange={(v) => setBundleForm({ ...bundleForm, pricing_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="derived">Derived (from items)</SelectItem>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {bundleForm.pricing_type === 'fixed' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bundle Labor Low ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bundleForm.bundle_labor_low}
                      onChange={(e) => setBundleForm({ ...bundleForm, bundle_labor_low: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Bundle Labor High ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bundleForm.bundle_labor_high}
                      onChange={(e) => setBundleForm({ ...bundleForm, bundle_labor_high: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bundle Materials Low ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bundleForm.bundle_materials_low}
                      onChange={(e) => setBundleForm({ ...bundleForm, bundle_materials_low: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Bundle Materials High ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bundleForm.bundle_materials_high}
                      onChange={(e) => setBundleForm({ ...bundleForm, bundle_materials_high: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Description</Label>
              <Textarea
                value={bundleForm.description}
                onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })}
                className="mt-1"
                placeholder="What's included in this bundle..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={bundleForm.is_active}
                onCheckedChange={(v) => setBundleForm({ ...bundleForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBundleDialog(false)}>Cancel</Button>
            <Button onClick={saveBundle} className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white">
              {editingBundle ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-on Dialog */}
      <Dialog open={showAddonDialog} onOpenChange={setShowAddonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAddon ? 'Edit Add-on' : 'Add Add-on'}</DialogTitle>
            <DialogDescription>Create a modifier that adjusts pricing</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={addonForm.name}
                onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                placeholder="e.g., High Ceilings (10-12 ft)"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Modifier Type *</Label>
                <Select 
                  value={addonForm.modifier_type} 
                  onValueChange={(v) => setAddonForm({ ...addonForm, modifier_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={addonForm.modifier_value}
                  onChange={(e) => setAddonForm({ ...addonForm, modifier_value: parseFloat(e.target.value) || 0 })}
                  placeholder={addonForm.modifier_type === 'percent' ? '15' : '100'}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Applies To *</Label>
                <Select 
                  value={addonForm.applies_to} 
                  onValueChange={(v) => setAddonForm({ ...addonForm, applies_to: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labor">Labor Only</SelectItem>
                    <SelectItem value="materials">Materials Only</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category (optional)</Label>
                <Input
                  value={addonForm.category}
                  onChange={(e) => setAddonForm({ ...addonForm, category: e.target.value })}
                  placeholder="Leave blank for all"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={addonForm.description}
                onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                className="mt-1"
                placeholder="Optional description..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={addonForm.is_active}
                onCheckedChange={(v) => setAddonForm({ ...addonForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddonDialog(false)}>Cancel</Button>
            <Button onClick={saveAddon} className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white">
              {editingAddon ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
